import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Users,
  Building2,
  Wallet,
  DollarSign,
  Globe,
  LifeBuoy,
  KeyRound,
  ToggleLeft,
  FileText,
  Palette,
  Shield,
  ScrollText,
  Settings,
  ChevronDown,
  LogOut,
  Tags,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SkipLink } from '@/components/SkipLink';
import { useAuth } from '@/lib/auth';

/** Routes grouped under the collapsible "Settings" section. */
const SETTINGS_PATHS = ['/sso', '/users', '/job-sources', '/taxonomy', '/gdpr'] as const;

/** Routes grouped under the collapsible "Logs" section. */
const LOGS_PATHS = ['/audit-log'] as const;

/**
 * Sidebar shell for the admin SPA. Matches the design mockup's admin
 * portal layout (sidebar + main pane). Sidebar entries map to the
 * Phase 8 sub-tasks 8.2 → 8.12.
 */
export function AdminLayout() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const signOut = useAuth((s) => s.signOut);

  async function handleSignOut() {
    await signOut();
    navigate('/sign-in', { replace: true });
  }

  return (
    <div className="flex min-h-screen bg-muted/30">
      <SkipLink />
      <aside className="hidden w-64 flex-shrink-0 border-r border-border bg-primary text-primary-foreground lg:flex lg:flex-col">
        <div className="border-b border-white/10 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-primary">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <p className="font-black">Admin Portal</p>
              <p className="text-xs text-slate-400">Platform control</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-3" aria-label="Admin">
          <SidebarLink to="/" end icon={BarChart3} label="Dashboard" />
          <SidebarLink to="/agencies" icon={Building2} label="Agencies" />
          <SidebarLink to="/payments" icon={Wallet} label="Payments" />
          <SidebarLink to="/plans" icon={DollarSign} label="Plans & pricing" />
          <SidebarLink to="/cv-templates" icon={Palette} label="CV templates" />
          <SidebarLink to="/support" icon={LifeBuoy} label="Support" />
          <SidebarLink to="/feature-flags" icon={ToggleLeft} label="Feature flags" />
          <SidebarLink to="/ai-cost" icon={BarChart3} label="AI cost" />

          <SidebarGroup icon={ScrollText} label="Logs" activePaths={LOGS_PATHS}>
            <SidebarLink to="/audit-log" icon={ScrollText} label="Audit log" />
          </SidebarGroup>

          <SidebarGroup icon={Settings} label="Settings" activePaths={SETTINGS_PATHS}>
            <SidebarLink to="/sso" icon={KeyRound} label="SSO providers" />
            <SidebarLink to="/users" icon={Users} label="Users" />
            <SidebarLink to="/job-sources" icon={Globe} label="Job sources" />
            <SidebarLink to="/taxonomy" icon={Tags} label="Job role taxonomy" />
            <SidebarLink to="/gdpr" icon={FileText} label="GDPR queue" />
          </SidebarGroup>
        </nav>

        <div className="border-t border-white/10 p-3">
          <div className="mb-3 px-2 text-xs">
            <p className="font-bold">{user?.display_name ?? '—'}</p>
            <p className="truncate text-slate-400">{user?.email ?? ''}</p>
          </div>
          <Button
            onClick={() => void handleSignOut()}
            variant="secondary"
            size="sm"
            className="w-full"
          >
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <main id="main" className="min-w-0 flex-1 px-4 py-8 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function SidebarGroup({
  icon: Icon,
  label,
  activePaths,
  children,
}: {
  icon: typeof BarChart3;
  label: string;
  activePaths: readonly string[];
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const hasActiveChild = activePaths.some((p) => pathname === p || pathname.startsWith(p + '/'));
  // Default open when a child route is active so the current page stays visible.
  const [open, setOpen] = useState(hasActiveChild);

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="mb-1 flex w-full items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-semibold text-slate-300 transition hover:bg-white/10 hover:text-white"
      >
        <Icon className="h-4 w-4 flex-shrink-0" aria-hidden /> {label}
        <ChevronDown
          className={`ml-auto h-4 w-4 flex-shrink-0 transition-transform ${open ? '' : '-rotate-90'}`}
          aria-hidden
        />
      </button>
      {open && <div className="ml-3 border-l border-white/10 pl-2">{children}</div>}
    </div>
  );
}

function SidebarLink({
  to,
  end = false,
  icon: Icon,
  label,
}: {
  to: string;
  end?: boolean;
  icon: typeof BarChart3;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `mb-1 flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
          isActive ? 'bg-white/15 text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'
        }`
      }
    >
      <Icon className="h-4 w-4 flex-shrink-0" aria-hidden /> {label}
    </NavLink>
  );
}
